import {
  memo,
  useEffectEvent,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type Ref,
} from 'react';
import { useTranslation } from 'react-i18next';
import { CloseIcon } from '@utils/icons';
// react-grid-layout v2 ships the `WidthProvider` HOC and the default `GridLayout`
// export from its `/legacy` entry. In v2 `Layout` is the array type and
// `LayoutItem` is a single grid item — the inverse of the v1 naming.
import GridLayout, {
  WidthProvider,
  type Layout,
  type LayoutItem,
  type ResizeHandleAxis,
} from 'react-grid-layout/legacy';
import 'react-grid-layout/css/styles.css';
import {
  CARD_TITLE_KEYS,
  GRID_COLS,
  GRID_MARGIN,
  type CardId,
  type DashboardLayoutItem,
} from './layout.defaults';
import styles from './dashboardGrid.module.css';

const ResponsiveGrid = WidthProvider(GridLayout);
const [MARGIN_X, MARGIN_Y] = GRID_MARGIN;

/* Custom corner grip, passed through RGL's `resizeHandle` prop — the library's
   intended customization point (replaces the earlier CSS-mask override of the
   default handle). The `react-resizable-handle(-se)` classes MUST stay on the
   element: the card-move DraggableCore uses `.react-resizable-handle` as its
   `cancel` selector (grabbing the grip resizes instead of dragging the card),
   and the bundled stylesheet hides handles outside edit mode via
   `.react-resizable-hide`. Visual styling lives in `styles.resizeHandle`. */
function renderResizeHandle(axis: ResizeHandleAxis, ref: Ref<HTMLElement>) {
  return (
    <span
      // RGL's callback types the ref as Ref<HTMLElement>; narrowing to
      // Ref<HTMLSpanElement> is safe because the rendered element IS a <span>.
      ref={ref as Ref<HTMLSpanElement>}
      className={`react-resizable-handle react-resizable-handle-${axis} ${styles.resizeHandle}`}
    >
      <svg viewBox="0 0 12 12" width="12" height="12" aria-hidden="true">
        <g stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
          <line x1="2.5" y1="9.5" x2="9.5" y2="2.5" />
          <line x1="6" y1="9.5" x2="9.5" y2="6" />
        </g>
      </svg>
    </span>
  );
}

/* Selector for the class RGL must treat as a drag-cancel zone — the per-card
   hide button (see CardHideButton). CSS Modules hashes the name, so it has to be
   built from the hashed value rather than written out as a literal. */
const DRAG_CANCEL = `.${styles.hideButton}`;

interface ICardHideButtonProps {
  id: CardId;
  onHide: (id: CardId) => void;
}

/* Per-card hide control: the ✕ in the cell's top-right corner, mounted only
   while editing. It sits inside the grid item, opposite the SE resize grip.

   Two independent guards keep pressing it from dragging the card (in edit mode
   the whole card is the drag handle):
     1. `draggableCancel` on the grid — the only guard that works for touch,
        because DraggableCore binds `touchstart` natively on the grid item, so a
        React handler on this button can never run first.
     2. stopPropagation on mousedown/touchstart — DraggableCore's `onMouseDown`
        is a React prop on the grid item, so this stops the mouse path at the
        source instead of relying on the selector match. */
const CardHideButton = memo(function CardHideButton({ id, onHide }: ICardHideButtonProps) {
  const { t } = useTranslation();
  const label = t('dashboard.layout.hideCard', { name: t(CARD_TITLE_KEYS[id]) });

  return (
    <button
      type="button"
      className={styles.hideButton}
      aria-label={label}
      title={label}
      onMouseDown={(event) => event.stopPropagation()}
      onTouchStart={(event) => event.stopPropagation()}
      onClick={(event) => {
        event.stopPropagation();
        onHide(id);
      }}
    >
      {/* 2.6 stroke, not the shared 1.6: at 10px the default weight reads as a
          smudge rather than an X. */}
      <CloseIcon size={10} strokeWidth={2.6} />
    </button>
  );
});

interface IDashboardGridProps {
  layout: DashboardLayoutItem[];
  editing: boolean;
  onLayoutChange: (next: DashboardLayoutItem[]) => void;
  cards: Record<CardId, ReactNode>;
  /** Hide a card from the layout. Renders the per-cell hide control while editing. */
  onHideCard?: (id: CardId) => void;
}

export function DashboardGrid({
  layout,
  editing,
  onLayoutChange,
  cards,
  onHideCard,
}: IDashboardGridProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [cell, setCell] = useState(0);

  // Resize quantization: RGL renders the actively-resizing card at the raw
  // cursor pixel size, so without this it follows the pointer freely. We pin
  // the card to its snapped WHOLE-CELL pixel size instead, so it jumps
  // cell-to-cell (locked to the grid). `resizeOverride` holds the current
  // target; the layout effect below re-applies it after every commit.
  const resizeOverride = useRef<{ node: HTMLElement; w: number; h: number } | null>(null);

  // True while a drag or resize is in flight. `measure()` (below) bails when set,
  // so the cell size stays frozen for the duration of the interaction. Without
  // this, RGL's mid-drag reflow resizes the measured first item, `measure`
  // recomputes `cell`, and the whole grid re-lays-out under the cursor — the
  // Visual Site Overview card (the first item, and the only one with its own
  // ResizeObserver) made this feedback loop visible. The ResizeObserver
  // re-measures normally once the interaction ends and the flag clears.
  const interactingRef = useRef(false);

  // Runs after every commit (child effects fire before parent effects, so this
  // executes after RGL has written the cursor-size style onto the grid item).
  // While a resize is active it overwrites that with the snapped cell size;
  // when idle the ref is null and it does nothing. No deps: it must re-assert
  // the pin on every render that happens mid-resize (RGL re-renders per tick).
  useLayoutEffect(() => {
    const override = resizeOverride.current;
    if (!override) return;
    override.node.style.width = `${override.w}px`;
    override.node.style.height = `${override.h}px`;
  });

  // Derive the square cell size from a card RGL has already laid out: a card of
  // width `w` measures `w*colWidth + (w-1)*margin` px, so
  // colWidth = (cardPx - (w-1)*margin) / w. This is exactly the colWidth RGL
  // used (whatever width WidthProvider resolved), so rows stay perfectly in sync
  // with columns — far more robust than measuring a wrapper element, which can
  // disagree with RGL's internal width.
  //
  // `measure` is an Effect Event so the layout effect runs once with a single
  // stable ResizeObserver while always reading the current first-card width —
  // without it, a resize of the first card would tear down and recreate the
  // observer (same pattern as AiAgentCard's runRestore).
  const firstW = layout[0]?.w ?? 1;
  const measure = useEffectEvent(() => {
    // Don't recompute the cell while dragging/resizing — see interactingRef.
    if (interactingRef.current) return;
    const item = wrapRef.current?.querySelector<HTMLElement>('.react-grid-item');
    const px = item?.offsetWidth ?? 0;
    if (px > 0) setCell((px - (firstW - 1) * MARGIN_X) / firstW);
  });
  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(wrap);
    const gridEl = wrap.querySelector('.react-grid-layout');
    if (gridEl) ro.observe(gridEl);
    // Also observe the first card itself: on mount WidthProvider renders one
    // frame at its default width before measuring the real one, and that
    // correction resizes only the items — wrap and gridEl (both width:100%)
    // never fire, which would leave `cell` stuck at the default-width value
    // (visibly non-square cells). Re-measuring on same-width item resizes is
    // free: setCell bails on an unchanged value.
    const firstItem = wrap.querySelector('.react-grid-item');
    if (firstItem) ro.observe(firstItem);
    return () => ro.disconnect();
  }, []);

  // Column width = measured cell (fractional, matches RGL's columns exactly).
  // Fall back to a sane desktop value if the canvas can't be measured yet, so
  // rows never collapse to 1px. rowHeight is the integer RGL receives.
  const colW = cell > 10 ? cell : 90;
  const rowHeight = Math.round(colW);
  const rglLayout = useMemo<LayoutItem[]>(() => layout.map((i) => ({ ...i })), [layout]);

  // Edit-mode snap grid: a faint rounded square for EVERY cell, positioned with
  // the exact math RGL uses (containerPadding is [0,0], so cell (x,y) sits at
  // x*(cell+margin), y*(cell+margin) and is cell×cell). Painted behind the grid,
  // so cards sit on their cells perfectly and the faint grid shows in the gutters
  // and wherever a card is dragged away. Recomputed only when the row count
  // changes (not on every drag tick).
  const maxRows = useMemo(
    () => layout.reduce((m, it) => Math.max(m, it.y + it.h), 0),
    [layout],
  );
  const allCells = useMemo(() => {
    const cells: { x: number; y: number }[] = [];
    for (let y = 0; y < maxRows; y++) {
      for (let x = 0; x < GRID_COLS; x++) cells.push({ x, y });
    }
    return cells;
  }, [maxRows]);

  return (
    <div ref={wrapRef} className={styles.wrap}>
      {editing && (
        <div className={styles.backdrop} aria-hidden="true">
          {allCells.map(({ x, y }) => (
            <span
              key={`${x},${y}`}
              className={styles.slot}
              style={{
                // Mirror RGL's own position math (containerPadding [0,0]):
                // left = round((colW+margin)*x), top = round((rowHeight+margin)*y),
                // a 1×1 cell is round(colW)=rowHeight square.
                left: Math.round((colW + MARGIN_X) * x),
                top: Math.round((rowHeight + MARGIN_X) * y),
                width: rowHeight,
                height: rowHeight,
              }}
            />
          ))}
        </div>
      )}
      <ResponsiveGrid
        className={`${styles.grid} ${editing ? styles.editing : ''}`}
        layout={rglLayout}
        cols={GRID_COLS}
        rowHeight={rowHeight}
        margin={GRID_MARGIN}
        // No outer container padding: cells (and the edit-mode square backdrop)
        // sit flush to the grid edge, so squares never appear in a margin
        // outside the cards.
        containerPadding={[0, 0]}
        isDraggable={editing}
        isResizable={editing}
        resizeHandle={renderResizeHandle}
        // Appended to RGL's own drag-cancel selector (it always keeps
        // `.react-resizable-handle`), so pressing a card's hide button never
        // starts a card drag. See CardHideButton.
        draggableCancel={DRAG_CANCEL}
        compactType="vertical"
        // Freeze cell measurement for the duration of a drag/resize (see
        // interactingRef): the reflow must not feed back into the cell size.
        onDragStart={() => { interactingRef.current = true; }}
        onDragStop={() => { interactingRef.current = false; }}
        onResizeStart={() => { interactingRef.current = true; }}
        // Pin the resizing card to its snapped whole-cell size (see
        // resizeOverride above). RGL's onResize gives us the already-snapped
        // integer w/h on `item`; we convert to pixels with the same math RGL
        // uses for a cell span and stash it for the layout effect to apply.
        // The SE grip is the only handle, so x/y never change mid-resize —
        // width/height are all that need pinning. `onLayoutChange` (below)
        // still persists the snapped w/h to the draft on every tick.
        onResize={(
          _layout: Layout,
          _old: LayoutItem | null,
          item: LayoutItem | null,
          _placeholder: LayoutItem | null,
          _event: Event,
          node: HTMLElement | null,
        ) => {
          if (!item || !node) return;
          resizeOverride.current = {
            node,
            w: Math.round(item.w * colW + (item.w - 1) * MARGIN_X),
            h: item.h * rowHeight + (item.h - 1) * MARGIN_Y,
          };
        }}
        onResizeStop={() => {
          resizeOverride.current = null;
          interactingRef.current = false;
        }}
        onLayoutChange={(next: Layout) =>
          onLayoutChange(
            next.map((rglItem) => ({
              // RGL widens `i` to string; re-narrowing is safe because the grid
              // is only ever given items keyed by CardId (see rglLayout above).
              i: rglItem.i as CardId,
              x: rglItem.x,
              y: rglItem.y,
              w: rglItem.w,
              h: rglItem.h,
              minW: rglItem.minW,
              minH: rglItem.minH,
            })),
          )
        }
      >
        {layout.map((item) => (
          // data-dashboard-editing is read by the shared card shell
          // (Cards/_shared/dashboardCard.module.css), which yields its
          // top-right header slot while the hide control occupies that corner.
          // An attribute rather than a class because CSS Modules hashes class
          // names per file, and the rule that reacts to it lives in the card's
          // stylesheet, not this one.
          <div key={item.i} className={styles.cell} data-dashboard-editing={editing || undefined}>
            {cards[item.i]}
            {editing && onHideCard && <CardHideButton id={item.i} onHide={onHideCard} />}
          </div>
        ))}
      </ResponsiveGrid>
    </div>
  );
}
